/**
 * One sentence that reads an agent's settings back in plain words, the same
 * wherever an agent is drawn: the creator (under the form, before Create) and
 * the Agents hub card. It is pure: a definition, its stored switches, and the
 * values its AI setting names.
 *
 *   "Estimates Effort for items added to a pool, using similar items and your
 *    sizing guide. Item managers and admins review. It starts off."
 */

import { resolveSources, resolveTriggers } from '../../components/agents/template/resolve';
import { capitalize } from '../automationRules';
import { DEFAULT_JOB_ROLES } from '../../types/prioritization/jobRoles';
import type { AgentSettings } from '../agentSettingsService';
import type { AgentBehaviorKind, AgentDefinition, AgentEventKey } from './agentDefinitions';

export interface DescribeAgentContext {
  /** Estimate type: the labels of the values whose AI setting names this agent. */
  valueLabels?: string[];
  /** Null or undefined settings: the agent is being made ("It starts off."). */
  settings?: AgentSettings | null;
  /** True while the creator is drafting: the status reads "starts", not "is". */
  isNew?: boolean;
}

const EVENT_WORDS: Record<AgentEventKey, string> = {
  item_created: 'each new item',
  item_updated: 'items whose text changes',
  item_added_to_pool: 'items added to a pool',
  item_added_to_cycle: 'items added to a cycle',
  readiness_failed: 'items that fail readiness',
  connector_sync_completed: 'items a connected tool changed',
  session_scheduled: 'the items of a scheduled session',
  priority_list_approved: 'the items of an approved priority list',
  task_assigned: 'the items of a task given to it',
};

/** What each evidence source is called in the sentence, by type where the type has its own word for it. */
const SOURCE_WORDS: Record<string, string | Partial<Record<AgentBehaviorKind, string>>> = {
  workspace_record: 'similar items',
  reference_docs: {
    estimate_value: 'your sizing guide',
    classify: 'your category examples',
    detect_cluster: 'your reference documents',
    propose_relationship: 'your reference documents',
    draft_items: 'your reference documents',
    find_issues: 'your reference documents',
  },
  connected_systems: 'connected history',
  item_text: 'the item itself',
  items_compared: 'the items it compares',
};

function joinWords(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

function agentDoesWhat(kind: AgentBehaviorKind, valueLabels: string[]): string {
  switch (kind) {
    case 'estimate_value':
      return valueLabels.length ? `Estimates ${joinWords(valueLabels)}` : 'Estimates a value';
    case 'classify':
      return 'Classifies items by category and altitude';
    case 'detect_cluster':
      return 'Finds duplicates';
    case 'propose_relationship':
      return 'Finds relationships between items';
    case 'draft_items':
      return 'Drafts items from text';
    case 'find_issues':
      return 'Finds issues in the workspace record';
  }
}

function agentRunsWhen(def: AgentDefinition, settings: AgentSettings | null): string {
  const t = resolveTriggers(def, settings);
  const parts: string[] = [];
  if (t.events.length) parts.push(`for ${joinWords(t.events.map((e) => EVENT_WORDS[e]))}`);
  if (t.scheduleMinutes) parts.push('on a schedule');
  if (!parts.length) return t.manualSingle || t.manualBulk || t.runNow ? 'when asked' : '';
  return parts.join(' and ');
}

function agentReadsFrom(def: AgentDefinition, settings: AgentSettings | null): string {
  const on = resolveSources(def, settings);
  // The documents are named only when there are some: the source may be on by default with nothing in it yet.
  const hasDocs = (settings?.referenceDocs?.length ?? 0) > 0;
  const words = def.evidenceSources
    .filter((s) => on[s.key] && (s.key !== 'reference_docs' || hasDocs))
    .map((s) => {
      const w = SOURCE_WORDS[s.key];
      if (!w) return s.label.toLowerCase();
      return typeof w === 'string' ? w : (w[def.behaviorKind] ?? 'your reference documents');
    });
  return words.length ? `using ${joinWords(words)}` : '';
}

function agentReviewedBy(def: AgentDefinition, settings: AgentSettings | null): string {
  const roles = settings?.reviewRoles?.length ? settings.reviewRoles : def.reviewRoles;
  const names = roles
    .map((slug) => DEFAULT_JOB_ROLES.find((r) => r.slug === slug)?.displayName ?? slug.replace(/-/g, ' '))
    .map((n) => `${n.toLowerCase()}s`);
  return names.length ? `${joinWords(names)} and admins review.` : 'Admins review.';
}

/** The read-back sentence for an agent as it is set, or as it is about to be made. */
export function describeAgent(def: AgentDefinition, ctx: DescribeAgentContext = {}): string {
  const settings = ctx.settings ?? null;
  const head = [agentDoesWhat(def.behaviorKind, ctx.valueLabels ?? []), agentRunsWhen(def, settings)].filter(Boolean).join(' ');
  const using = agentReadsFrom(def, settings);
  const first = using ? `${head}, ${using}.` : `${head}.`;
  const enabled = settings?.enabled ?? false;
  const status = ctx.isNew || !settings ? (enabled ? 'It starts on.' : 'It starts off.') : enabled ? 'It is on.' : 'It is off.';
  return `${capitalize(first)} ${capitalize(agentReviewedBy(def, settings))} ${status}`;
}
